import React, { useEffect, useRef, useState } from 'react';
import { getGamesRequest } from '../../httpRequests/consoleRequests';
import IGame from '../../interfaces/game';
import { useTranslation } from '../../hooks/useTranslation';
import logo from '../../assets/images/logoImg.png';
import launchClasses from '../../containers/launch/launch.module.scss';
import classes from './gameShowcase.module.scss';
import { gameArticles } from '../../consts/gameArticles';
import english from '../../i18n/locales/en/translation.json';

const gamePaths = new Set(['/slipnbump', '/bubblewargame', '/partygames', '/capturetheflag', '/racensmash', '/roperagegame', '/icehockygame', '/ballfrenzygame', '/drawgame', '/rafttogether', '/warfare', ...gameArticles].map(path => path.toLowerCase()));
const gameHref = (game: IGame) => {
  const path = '/' + (game.path || '').replace(/^\/+|\/+$/g, '');
  return gamePaths.has(path.toLowerCase()) ? path : '/specificGame?gameKey=' + encodeURIComponent(game.key);
};

export default function GameShowcase({ catalog = false }: { catalog?: boolean }) {
  const [games, setGames] = useState<IGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(0);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  useEffect(() => {
    const element = detailsRef.current;
    if (!element) return;
    const update = () => setCanScroll(element.scrollHeight - element.clientHeight - element.scrollTop > 4);
    element.scrollTop = 0;
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : undefined;
    observer?.observe(element);
    window.addEventListener('resize', update);
    return () => { observer?.disconnect(); window.removeEventListener('resize', update); };
  }, [active, games]);
  const previousActive = useRef(0);
  useEffect(() => { previousActive.current = active; }, [active]);
  const [paused, setPaused] = useState(false);
  const [retry, setRetry] = useState(0);
  const { getTranslation } = useTranslation();
  const t = getTranslation('gameShowcase') || english.gameShowcase;
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getGamesRequest().then(data => {
      if (cancelled) return;
      const testing = localStorage.getItem('rollout') === 'testing';
      setGames(data.filter(game => testing || (game.rollout === 'production' && !game.earlyAccess)));
      setActive(0);
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [retry]);
  useEffect(() => {
    if (catalog || paused || games.length < 2) return;
    const timer = window.setTimeout(() => setActive(index => (index + 1) % games.length), 5000);
    return () => window.clearTimeout(timer);
  }, [active, catalog, paused, games.length]);
  const copy = (game: IGame) => {
    const translation = getTranslation('gamekeys' + game.key);
    return { name: translation?.name || game.name, description: translation?.description || game.description };
  };
  const players = (game: IGame) => t.players.replace('{{min}}', String(game.minNumberOfPlayers)).replace('{{max}}', String(game.maxNumberOfPlayers));
  return <section className={catalog ? classes.catalog : classes.showcase} aria-label={t.browseGames}>
    {catalog && <a href="/" className={classes.brand} aria-label={t.home}><img src={logo} alt="" />PlayCloud</a>}
    <header className={classes.header}>
      {catalog ? <h1>{t.catalogTitle}</h1> : <h2>{t.showcaseTitle}</h2>}
      <p>{t.subtitle}</p>
    </header>
    {loading && <p className={classes.status} role="status">{t.loading}</p>}
    {error && <div className={classes.status} role="alert">{t.loadError} <button onClick={() => setRetry(value => value + 1)}>{t.retry}</button></div>}
    {!loading && !error && !games.length && <p className={classes.status}>{t.empty}</p>}
    {!loading && !error && games.length > 0 && (catalog ? <>
      <p className={classes.count}>{t.gameCount.replace('{{count}}', String(games.length))}</p>
      <div className={classes.grid}>
        {games.map(game => <a key={game.key} href={gameHref(game)} className={classes.card}>
          <img src={game.imgUrl} alt="" loading="lazy" />
          <div className={classes.cardDetails}>
            <h2>{copy(game).name}</h2><span className={classes.players}>{players(game)}</span>
            <p>{copy(game).description}</p>
          </div>
        </a>)}
      </div>
    </> : <>
      <div className={classes.carousel} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setPaused(false); }}>
        <div className={classes.viewport}>
          {games.map((game, index) => {
            let offset = (index - active + games.length) % games.length;
            if (offset > games.length / 2) offset -= games.length;
            let previousOffset = (index - previousActive.current + games.length) % games.length;
            if (previousOffset > games.length / 2) previousOffset -= games.length;
            const wrapped = Math.abs(offset - previousOffset) > 1;
            return <a key={game.key} href={gameHref(game)} aria-label={copy(game).name}
              aria-current={index === active ? 'true' : undefined} tabIndex={Math.abs(offset) <= 2 ? 0 : -1}
              className={`${classes.slide} ${offset === 0 ? classes.focused : ''}`}
              style={{ '--offset': offset + Math.sign(offset) * 0.35, transition: wrapped ? 'none' : undefined } as React.CSSProperties}>
              <img src={game.imgUrl} alt="" loading="lazy" />
              <div className={classes.featureDetails} aria-hidden={offset !== 0}
                ref={offset === 0 ? detailsRef : undefined}
                onScroll={event => {
                  if (offset !== 0) return;
                  const element = event.currentTarget;
                  setCanScroll(element.scrollHeight - element.clientHeight - element.scrollTop > 4);
                }}>
                <h3>{copy(game).name}</h3><span className={classes.players}>{players(game)}</span>
                <p>{copy(game).description}</p>
              </div>
              {offset === 0 && canScroll && <span className={classes.scrollHint}>{t.scrollHint} <span aria-hidden="true">↓</span></span>}
            </a>;
          })}
        </div>
        <button className={`${classes.arrow} ${classes.previous}`} aria-label={t.previousGame} disabled={games.length < 2}
          onClick={() => setActive(index => (index - 1 + games.length) % games.length)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <button className={`${classes.arrow} ${classes.next}`} aria-label={t.nextGame} disabled={games.length < 2}
          onClick={() => setActive(index => (index + 1) % games.length)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m10 6 6 6-6 6" /></svg>
        </button>
      </div>
      <a href="/browse" className={`${launchClasses.playButton} ${classes.browseButton}`}>{t.browseGames}</a>
    </>)}
  </section>;
}
